import { ApiErrorMessages } from '@/components/element/ApiErrorMessages';
import { FormGroup } from '@/components/element/Form/FormGroup';
import { InputPrice } from '@/components/element/Form/InputPrice';
import { ScrollTop } from '@/components/element/ScrollTop';
import { MenuButtonGroup } from '@/components/features/SettingMenuButtonGroup';
import { WorkspaceLayout } from '@/components/layout/WorkspaceLayout';
import { useInvalidateProducts, useProducts } from '@/hooks/api/0390_products';
import { useUpdateProduct } from '@/hooks/api/0420_update_product';
import { Product } from '@/hooks/api/models';
import { toastSuccess } from '@/lib/toast';
import { useLoading } from '@/providers/LoadingProvider';
import { useNavState } from '@/providers/NavStateProvider';
import { useCurrentWorkspace } from '@/providers/WorkspaceProvider';
import { useEffect, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { Link, useNavigate, useParams } from 'react-router-dom';

type FormInputs = {
  identifier: string;
  id: number;
  name: string;
  description: string;
  spot_available: boolean;
  spot_amount?: number;
  monthly_available: boolean;
  monthly_amount?: number;
  contract_term_available: boolean;
  delivery_date_available: boolean;
  invalidity: boolean;
};

export const WorkspaceProductEdit = () => {
  const { setTitle, setBreadcrumbs } = useNavState();
  useEffect(() => setTitle('売上カテゴリ編集'), []);
  useEffect(() => setBreadcrumbs([{ label: `売上カテゴリ設定`, path: `/w/${_w_!.identifier}/setting/products` }]), []);

  const navigate = useNavigate();
  const [product, setProduct] = useState<Product>();
  const { id: product_id } = useParams();
  const { _w_, me } = useCurrentWorkspace();
  const { data: products } = useProducts({ identifier: _w_!.identifier, include_invalidity: true });
  const { setShowLoading } = useLoading();
  const { mutate, isPending, data, error } = useUpdateProduct();
  const { invalidate } = useInvalidateProducts({ identifier: _w_!.identifier });

  useEffect(() => {
    if (products) {
      const _product = products.find((p) => p.id == Number(product_id));
      if (_product) {
        setProduct(_product);
      } else {
        navigate(`/w/${_w_!.identifier}/setting/products`);
      }
    }
  }, [products]);

  const {
    control,
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FormInputs>({
    mode: 'onBlur',
    criteriaMode: 'all',
    defaultValues: {
      identifier: _w_!.identifier,
      contract_term_available: true,
    },
  });
  const spotAvailable = watch('spot_available');
  const monthlyAvailable = watch('monthly_available');

  useEffect(() => {
    if (product) {
      setValue('id', product.id);
      setValue('name', product.name);
      setValue('description', product.description || '');
      setValue('spot_available', product.spot_available);
      setValue('spot_amount', product.spot_amount);
      setValue('monthly_available', product.monthly_available);
      setValue('monthly_amount', product.monthly_amount);
      // setValue('contract_term_available', product.contract_term_available);
      setValue('delivery_date_available', product.delivery_date_available);
      setValue('invalidity', product.invalidity);
    }
  }, [product]);

  useEffect(() => setShowLoading(isPending), [isPending]);

  useEffect(() => {
    if (data) {
      toastSuccess('売上カテゴリを編集しました');
      invalidate(); // 売上カテゴリのキャッシュクリア
      navigate(`/w/${_w_!.identifier}/setting/products`);
    }
  }, [data]);

  const onSubmit: SubmitHandler<FormInputs> = (data) => {
    setShowLoading(true);
    mutate(data);
  };

  return (
    <WorkspaceLayout>
      <ScrollTop />
      <div className="container-fluid py-4">
        <MenuButtonGroup selected={'product'} />

        <div className="row">
          <div className="col-12 col-lg-9 mx-auto">
            <div className="card mb-4" id="basic-info">
              <div className="card-header d-flex align-items-center">
                <div className="col-md-8">
                  <h5 className="mb-0">売上カテゴリ編集</h5>
                </div>
              </div>
              <form onSubmit={handleSubmit(onSubmit)}>
                <div className="card-body pt-0">
                  <ApiErrorMessages error={error} />

                  <div className="col-md-12">
                    <div className="form-group">
                      <label>
                        売上カテゴリ名<span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        maxLength={50}
                        className={`form-control ${errors.name && 'is-invalid'}`}
                        placeholder={'売上カテゴリ名'}
                        {...register('name', {
                          required: '入力して下さい。',
                        })}
                      />
                    </div>
                    <div className="form-group">
                      <label>メモ</label>
                      <textarea
                        rows={5}
                        className={`form-control ${errors.description && 'is-invalid'}`}
                        {...register('description')}
                      ></textarea>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-12 col-md-6">
                      <div className="form-group">
                        <label>一括請求金額入力フォーム</label>
                        <div className="form-check form-switch">
                          <input
                            type="checkbox"
                            id="on-spot-available"
                            className="form-check-input"
                            {...register('spot_available')}
                          />
                          <label className="form-check-label" htmlFor="on-spot-available">
                            一括請求金額入力欄を表示します
                          </label>
                        </div>
                      </div>
                    </div>
                    {spotAvailable && (
                      <div className="col-12 col-md-5">
                        <FormGroup label="一括請求金額初期値">
                          <div className="input-group">
                            <Controller
                              render={({ field: { onChange, value, ...field } }) => (
                                <InputPrice onChange={onChange} value={value} error={errors.spot_amount} />
                              )}
                              control={control}
                              name="spot_amount"
                            />
                          </div>
                          <div className="text-xs text-muted mt-1">
                            一括請求金額入力欄にデフォルトで入力したい値がある場合は設定してください。
                          </div>
                        </FormGroup>
                      </div>
                    )}
                  </div>
                  <div className="row">
                    <div className="col-12 col-md-6">
                      <div className="form-group">
                        <label>月額請求金額入力フォーム</label>
                        <div className="form-check form-switch">
                          <input
                            type="checkbox"
                            id="on-monthly-available"
                            className="form-check-input"
                            {...register('monthly_available')}
                          />
                          <label className="form-check-label" htmlFor="on-monthly-available">
                            月額請求金額入力欄を表示します
                          </label>
                        </div>
                      </div>
                    </div>
                    {monthlyAvailable && (
                      <>
                        <div className="col-12 col-md-5">
                          <FormGroup label="月額請求金額初期値">
                            <div className="input-group">
                              <Controller
                                render={({ field: { onChange, value, ...field } }) => (
                                  <InputPrice onChange={onChange} value={value} error={errors.monthly_amount} />
                                )}
                                control={control}
                                name="monthly_amount"
                              />
                            </div>
                            <div className="text-xs text-muted mt-1">
                              月額請求金額入力欄にデフォルトで入力したい値がある場合は設定してください。
                            </div>
                          </FormGroup>
                        </div>
                      </>
                    )}
                  </div>
                  {/* <div className="form-group">
                    <label>Contract Term Form</label>
                    <div className="form-check form-switch">
                      <input type="checkbox" id="on-contract-term-available" className="form-check-input" {...register('contract_term_available')} />
                      <label className="form-check-label" htmlFor="on-contract-term-available">
                        契約期間の入力欄を表示します
                      </label>
                    </div>
                  </div> */}
                  <div className="form-group">
                    <label>納品日入力フォーム</label>
                    <div className="form-check form-switch">
                      <input
                        type="checkbox"
                        id="on-delivery-date-available"
                        className="form-check-input"
                        {...register('delivery_date_available')}
                      />
                      <label className="form-check-label" htmlFor="on-delivery-date-available">
                        納品日の入力欄を表示します
                      </label>
                    </div>
                  </div>
                  <div className="form-group">
                    <label>無効化</label>
                    <div className="form-check form-switch">
                      <input
                        type="checkbox"
                        id="on-invalidity"
                        className="form-check-input"
                        {...register('invalidity')}
                      />
                      <label className="form-check-label" htmlFor="on-invalidity">
                        プロジェクト作成時の売上カテゴリ候補に表示されなくなります
                      </label>
                    </div>
                  </div>
                </div>
                <div className="card-footer d-flex align-items-center justify-content-end pt-0">
                  <Link to={`/w/${_w_!.identifier}/setting/products`} className="btn btn-outline-secondary me-2">
                    戻る
                  </Link>
                  <button className="btn bg-gradient-dark btn-icon">
                    <i className="fas fa-check-circle"></i>変更を確定する
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </WorkspaceLayout>
  );
};
